import * as React from 'react';
import styled from 'styled-components';

import { BREAKPOINTS, COLORS } from '../../styles';
import { NavButton, NavOption, ActionOptions, UserMenuItem } from '../../types';
import Actions, { ActionButton } from '../Actions';
import { Close, GridView, Notification } from '../../icons';
import MobileActionMenu from './MobileActionMenu';
import { Right, RowLayout, MenuWrapper, MenuSection, MenuSectionList } from './CommonStyles';
import { NavLink, useLocation } from 'react-router-dom';
import { SwitcherMenuItem } from '../../Switcher';
import Avatar, { AvatarContainer } from '../Avatar';
import MobileUserMenu from '../UserMenu/MobileUserMenu';
import LaerdalLogo from '../../assets/laerdal-logo.svg';
import { UIIconButton } from '../../Button';

const SwitchButton = styled.span`
  color: ${COLORS.neutral_600};
  font-size: 14px;
  font-weight: bold;
  padding: 0;
  border: 0;
  margin: 0 auto 0 12px;
  line-height: 48px;
`;

const BottomPinnedSection = styled.div`
  position: absolute;
  left: 0;
  width: 100%;
  bottom: 0;
  border-top: 1px solid ${COLORS.neutral_200};
`;

const FirstRowLayout = styled(RowLayout)`
  margin-top: 10px;
  padding: 12px;
`;

const BottomRowLayout = styled(RowLayout)`
  margin: 8px auto 0 0;
  width: 95%;
  height: 48px;
  ${BREAKPOINTS.SMALL} {
    height: 56px;
  }
`;

const StyledNavItem = styled(SwitcherMenuItem)`
  font-style: normal;
  font-weight: normal;
  font-size: 19px;
  padding: 0 0 0 20px;
  margin: 0 2px;

  display: flex;
  align-items: center;
  text-align: center;
  position: relative;

  color: ${COLORS.black};
  text-decoration: none;

  svg {
    margin-right: 16px;
  }

  &:after {
    content: '';
    display: block;
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background-color: transparent;
  }

  &.active:not(.disabled) {
    font-weight: bold;
    &:after {
      background-color: ${COLORS.primary_500};
    }

    color: ${COLORS.primary_800};
    svg path {
      fill: ${COLORS.primary_800};
    }
  }

  &.disabled {
    color: ${COLORS.neutral_300};
    background-color: ${COLORS.neutral_100};
  }

  &:hover:not(.disabled) {
    color: ${COLORS.primary_600};
    background-color: ${COLORS.primary_20};

    svg path {
      fill: ${COLORS.primary_600};
    }
  }

  &:active:not(.disabled) {
    color: ${COLORS.primary_800};
    background-color: ${COLORS.primary_100};
    box-shadow: none;
    &:after {
      background-color: ${COLORS.primary_500};
    }
    svg path {
      fill: ${COLORS.primary_800};
    }
  }

  &:focus:not(.disabled) {
    box-shadow: 0px 0px 8px ${COLORS.primary_500}, 0px 4px 12px rgba(46, 127, 161, 0.25);
    outline: none;
  }
`;
const SwitcherButton = styled.div`
  display: flex;
  margin-left: auto;

  line-height: 48px;
  button:last-child {
    margin: 0 !important;
  }
`;

type Props = {
  name: string;
  navigationOptions?: NavOption[];
  secondaryNavigationOptions?: NavOption[];
  secondaryButton?: NavButton;
  actions?: ActionOptions;
  clickMenuAction: () => void;
  firstName: string;
  lastName: string;
  email: string;
  signout?: (e: any) => void;
  isAuthenticated?: boolean;
  notifications?: NavOption[];
  switcherAction?: () => void;
  accountSection?: UserMenuItem[];
  organizationSection?: UserMenuItem[];
  supportSection?: UserMenuItem[];
  accountMenuHeader: string;
  signOutLabel: string;
  organizationName: string;
  useUserMenu: boolean | undefined;
  showUserMenu: boolean;
  setShowUserMenu: (show: boolean) => void;
};

const MobileMenu = ({
  clickMenuAction,
  navigationOptions,
  actions,
  firstName,
  lastName,
  email,
  signout,
  notifications,
  accountSection,
  organizationSection,
  supportSection,
  switcherAction,
  accountMenuHeader,
  signOutLabel,
  organizationName,
  useUserMenu,
  showUserMenu,
  setShowUserMenu,
}: Props) => {
  const [showNotificationMenu, setShowNotificationMenu] = React.useState<boolean>(false);
  const notificationMenuRef = React.useRef(null);
  const userMenuRef = React.useRef(null);
  const userRef = React.useRef(null);
  const notificationRef = React.useRef(null);
  const switcherRef = React.useRef(null);

  const location = useLocation();

  if (actions?.user) {
    actions.user = () => setShowUserMenu(true);
  }
  if (actions?.notification) {
    actions.notification = () => setShowNotificationMenu(true);
  }

  const fireAction = (action: (() => void) | undefined, ref: React.MutableRefObject<HTMLButtonElement | null>) => {
    ref?.current?.blur();
    action !== undefined && action();
  };

  const isPressingEnter = (e: any) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      return true;
    }
    return false;
  };

  React.useEffect(() => {
    clickMenuAction();
  }, [location]);

  return (
    <>
      {actions?.notification && (
        <MenuWrapper ref={notificationMenuRef} className={showNotificationMenu ? 'open' : ''} isVisible={showNotificationMenu}>
          <MobileActionMenu
            name="Notifications"
            clickMenuAction={() => setShowNotificationMenu(false)}
            navigationOptions={notifications}
            actionList={{
              notification: () => {},
            }}
          />
        </MenuWrapper>
      )}
      {actions?.user && (
        <MenuWrapper ref={userMenuRef} className={showUserMenu ? 'open' : ''} isVisible={showUserMenu}>
          <MobileUserMenu
            accountMenuHeader={accountMenuHeader}
            firstName={firstName}
            lastName={lastName}
            email={email}
            organizationName={organizationName}
            clickMenuAction={clickMenuAction}
            accountSection={accountSection}
            organizationSection={organizationSection}
            supportSection={supportSection}
            signOut={signout}
            signOutLabel={signOutLabel}
          />
        </MenuWrapper>
      )}
      <FirstRowLayout>
        {firstName && lastName && (
          <AvatarContainer
            id="avatarContainer"
            ref={userRef}
            onClick={actions?.user}
            onKeyDown={(e: any) => (isPressingEnter(e) ? fireAction(actions?.user, userRef) : null)}
            inMobileMenu={true}
            hideOnLowWidth={false}>
            <Avatar size={36} firstName={firstName} lastName={lastName} color={COLORS.primary_500} profileMenuLink="#TODO" />
          </AvatarContainer>
        )}
        {actions?.notification && (
          <ActionButton
            hideOnLowWidth={false}
            inMobileMenu={true}
            ref={notificationRef}
            onClick={() => fireAction(actions?.notification, notificationRef)}
            onKeyDown={(e: any) => (isPressingEnter(e) ? fireAction(actions?.notification, notificationRef) : null)}>
            <div>
              <Notification size="24px" />
            </div>
          </ActionButton>
        )}
        <Right>
          <Actions
            actions={actions}
            firstName={firstName}
            lastName={lastName}
            isInMobileMenu={true}
            hideOnLowWidth={false}
            useUserMenu={useUserMenu}
            setShowUserMenu={setShowUserMenu}
          />
          <UIIconButton action={() => clickMenuAction()} isInMobileMenu={true}>
            <Close size="24px" />
          </UIIconButton>
        </Right>
      </FirstRowLayout>
      <MenuSection>
        <MenuSectionList>
          {navigationOptions?.map(no => (
            <li key={no.label}>
              <StyledNavItem
                as={NavLink}
                exact={no?.exact}
                to={no.to}
                onClick={() => {
                  if (no?.onClick) {
                    no?.onClick();
                  }
                }}>
                {no.label}
              </StyledNavItem>
            </li>
          ))}
        </MenuSectionList>
      </MenuSection>

      <BottomPinnedSection>
        <MenuSection style={{ padding: '0 12px 12px 12px', margin: '0' }}>
          <MenuSectionList>
            <BottomRowLayout>
              <LaerdalLogo />
              {switcherAction && (
                <SwitcherButton>
                  <SwitchButton onClick={() => switcherAction()}>Switch site</SwitchButton>{' '}
                  <ActionButton
                    hideOnLowWidth={false}
                    inMobileMenu={true}
                    id="globalNavAppsButton"
                    ref={switcherRef}
                    onClick={() => switcherAction()}
                    onKeyDown={(e: any) => (isPressingEnter(e) ? switcherAction() : null)}>
                    <div>
                      <GridView size="24px" />
                    </div>
                  </ActionButton>
                </SwitcherButton>
              )}
            </BottomRowLayout>
          </MenuSectionList>
        </MenuSection>
      </BottomPinnedSection>
    </>
  );
};

export default MobileMenu;
